/**
 * Back in Stock triggers on product cards.
 *
 * Klaviyo's Back in Stock form resolves which product it is subscribing someone
 * to by fetching the current pathname with `.js` appended — Shopify's product
 * JSON endpoint — and reading `?variant=` for the variant. That works on a
 * product page and nowhere else: on a collection page it requests
 * /collections/<handle>.js, gets a 404, and opens with no product attached.
 *
 * So before opening the form from a card, the address bar is pointed at that
 * card's product URL with history.replaceState (no navigation, no reload), and
 * restored once the form closes. Klaviyo re-reads the URL whenever it changes,
 * which is why the restore waits for the form to close rather than firing
 * immediately — restoring early would detach the subscription mid-form. The
 * restore does provoke one harmless 404 for <collection>.js as Klaviyo looks for
 * a product again.
 *
 * The popup is an unclassed div at the end of the body which Klaviyo hides rather
 * than removes on close, so the close is detected by polling its visibility. Its
 * class names are build-hashed, hence nothing here selects on them.
 *
 * Caveat for future maintenance: this depends on Klaviyo resolving products from
 * location, which is undocumented and can change without notice. If card
 * triggers start opening an empty form, check whether that <path>.js request is
 * still being made. The supported alternative is a theme-built modal posting to
 * https://a.klaviyo.com/client/back-in-stock-subscriptions/.
 *
 * The click handler is delegated from the document because facet filtering
 * replaces the whole product grid (see facets.js), destroying and recreating
 * every card.
 */
if (!window.klaviyoBisInit) {
  window.klaviyoBisInit = true;

  (() => {
    const TRIGGER = '.klaviyo-bis-trigger';
    const POLL_MS = 250;
    // Safety net only, for a popup this script somehow never sees close.
    const MAX_WAIT_MS = 15 * 60 * 1000;

    let restoreUrl = null;
    let poll = null;

    const restore = () => {
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
      if (!restoreUrl) return;
      window.history.replaceState({}, '', restoreUrl);
      restoreUrl = null;
    };

    // Klaviyo's popup is an unclassed div at the end of the body, and on close it
    // is hidden rather than removed — so the close is detected by visibility, not
    // by the node going away. Its container may also exist from page load, hidden,
    // which is why what's tracked is "a body child that became visible".
    const isVisible = el =>
      el.offsetParent !== null || el.getClientRects().length > 0;

    const visibleChildren = () =>
      new Set([...document.body.children].filter(isVisible));

    const watchForClose = wasVisible => {
      const startedAt = Date.now();
      let popup = null;

      poll = setInterval(() => {
        if (Date.now() - startedAt > MAX_WAIT_MS) return restore();

        if (!popup) {
          popup = [...document.body.children].find(
            el => isVisible(el) && !wasVisible.has(el)
          );
          return;
        }

        if (!popup.isConnected || !isVisible(popup)) restore();
      }, POLL_MS);
    };

    document.addEventListener('click', event => {
      const trigger = event.target.closest(TRIGGER);
      if (!trigger) return;

      const formId = window.klaviyoBisFormId;
      const variantId = trigger.dataset.variant;
      const card = trigger.closest('card-product');
      const productUrl = card?.dataset.productUrl;

      // Without any of these the form cannot resolve a product, so fall through
      // to the anchor's href and let the customer land on the product page.
      if (!formId || !variantId || !productUrl) return;
      if (typeof window.klaviyo?.openForm !== 'function') return;

      event.preventDefault();

      // Guard against a second card being clicked while a form is already open,
      // which would otherwise lose the original URL.
      if (restoreUrl) restore();

      restoreUrl = window.location.pathname + window.location.search;
      window.history.replaceState(
        {},
        '',
        `${productUrl}?variant=${variantId}`
      );

      watchForClose(visibleChildren());
      window.klaviyo.openForm(formId);
    });

    // Never leave a spoofed URL behind for a refresh or a shared link to land on.
    window.addEventListener('beforeunload', restore);
  })();
}
